import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Link,
} from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyRegistrationOtp } = useAuth();

  const legacyToken = searchParams.get('token');
  const [email, setEmail] = useState(() => String(searchParams.get('email') || '').trim());
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const q = searchParams.get('email');
    if (q) setEmail(String(q).trim());
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const em = email.trim().toLowerCase();
    if (!em) {
      setError('Enter the email you signed up with.');
      return;
    }
    const digits = otpCode.replace(/\D/g, '');
    if (digits.length !== 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setSubmitting(true);
    const result = await verifyRegistrationOtp(em, digits);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Verification failed.');
    }
    setSubmitting(false);
  };

  const handleResend = async () => {
    const em = email.trim().toLowerCase();
    if (!em) {
      setError('Enter your email first, then resend.');
      return;
    }
    setResending(true);
    setError('');
    try {
      await api.post('/auth/resend-verification', { email: em });
    } catch {}
    setResending(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ textAlign: 'center', maxWidth: 420, width: '100%' }}>
        <MailOutline sx={{ fontSize: 64, color: PRIMARY, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 1 }}>
          Verify your email
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
          Enter the 6-digit code we sent you. Codes expire in 10 minutes.
        </Typography>

        {legacyToken && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1, textAlign: 'left' }}>
            Email links are no longer used. Enter your email and the verification code from your latest email, or use <b>Resend code</b> on the sign-in page.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1, textAlign: 'left' }}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          />
          <TextField
            fullWidth
            label="6-digit code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code', 'aria-label': 'Verification code' }}
            placeholder="000000"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.15em' } }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              py: 1.25, mb: 2, bgcolor: PRIMARY, borderRadius: 1,
              fontSize: '14px', fontWeight: 600, textTransform: 'none',
              boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
            }}
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </form>

        <Button
          variant="outlined"
          onClick={handleResend}
          disabled={resending}
          sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 3, mb: 2 }}
        >
          {resending ? 'Sending…' : 'Resend code'}
        </Button>

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <Link component={RouterLink} to="/login" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>
            Back to sign in
          </Link>
          {' · '}
          <Link component={RouterLink} to="/register" sx={{ color: PRIMARY, textDecoration: 'none', fontWeight: 600 }}>
            Create account
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
